import { Source } from '../../types.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  BULK_FETCH_TIMEOUT,
  OfflineQueue,
  sourceToServer,
  sourceFromServer,
} from './base.ts';

export const sourcesApi = {
  getSources: async (projectId: string): Promise<Source[]> => {
    try {
      // cache: 'no-store' — zie getPeople in people.ts (Refs #912).
      const res = await fetchWithTimeout(`/api/projects/${projectId}/sources`, {
        cache: 'no-store',
      });
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map(sourceFromServer);
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty sources list:', error);
      return [];
    }
  },

  saveSource: async (source: Partial<Source>): Promise<Source> => {
    const isUpdate = !!source.id;
    const serverPayload = sourceToServer(source);

    try {
      if (isUpdate) {
        const res = await fetchWithTimeout(`/api/sources/${source.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return sourceFromServer(await res.json());
      } else {
        const res = await fetchWithTimeout(`/api/projects/${source.projectId}/sources`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return sourceFromServer(await res.json());
      }
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      const tempId = source.id || crypto.randomUUID();
      OfflineQueue.enqueue({
        operation: isUpdate ? 'UPDATE' : 'CREATE',
        endpoint: isUpdate
          ? `/api/sources/${source.id}`
          : `/api/projects/${source.projectId}/sources`,
        method: isUpdate ? 'PUT' : 'POST',
        data: source,
      });
      return {
        ...source,
        id: tempId,
        // #1276 (NC #578) — zonder aanmaakdatum kan een offline aangemaakte bron
        // niet op recentheid gesorteerd worden. `byNewestFirst` vangt dat ook af,
        // maar hier invullen is eerlijker: de bron is nú gemaakt.
        createdAt: source.createdAt || new Date().toISOString(),
        _offline: true,
      } as any;
    }
  },

  deleteSource: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/sources/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/sources/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  bulkCreateSources: async (
    projectId: string,
    sources: {
      gedId: string;
      title: string;
      author?: string;
      url?: string;
      description?: string;
      sourceType?: string;
      reliability?: string;
      citation?: string;
      _repoRef?: string;
      _resolvedRepoId?: string;
      _callNumber?: string;
      _mediaType?: string;
      typeGuessed?: boolean;
    }[]
  ): Promise<{ sources: { id: string; gedId: string }[]; mergedCount: number }> => {
    const serverSources = sources.map((s) => ({
      gedId: s.gedId,
      title: s.title,
      author: s.author || null,
      notes: s.description || null,
      source_type: s.sourceType || 'Other',
      url: s.url || null,
      citation: s.citation || null,
      reliability: s.reliability || 'unknown',
      repository_id: s._resolvedRepoId || null,
      call_number: s._callNumber || null,
      media_type: s._mediaType || null,
      typeGuessed: s.typeGuessed === true,
    }));
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/sources/bulk`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sources: serverSources }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return { sources: data.sources || [], mergedCount: data.mergedCount || 0 };
  },

  /** Start de bronverificatie bij Open Archieven (Pro/Business); antwoordt direct. */
  triggerVerification: async (projectId: string): Promise<{ started: boolean }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/sources/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  mergeSources: async (targetId: string, sourceId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/sources/${targetId}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceId }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  getSourceMedia: async (
    projectId: string,
    sourceId: string
  ): Promise<
    {
      id: string;
      mediaId: string;
      filename: string;
      filePath: string;
      mimeType: string;
      caption: string;
      fileSize: number;
    }[]
  > => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/entity-media/source/${sourceId}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
